'use client';

import { useMemo } from 'react';

import { locationName } from '@domain/models/activityLocation/displayName';
import type {
  OnlineOption,
  PhysicalLocationOption,
  SlotLocationBlockTranslations
} from '@ui/components/features/locationLibrary';

import { useActivityLocations } from './useActivityLocations';
import { useSlotLocationPickerTranslations } from './useSlotLocationPickerTranslations';

/**
 * The bundle of props `CreateSlotSession` needs to render its Location block:
 * the SELECTED activity's locations split into in-person (physical) and online
 * options, plus the block strings. The block is PICK-ONLY; adding / editing a
 * location lives in the activity wizard, reached via the panel's "Manage
 * locations" link (the panel's `editActivityHref`).
 *
 * The options are sourced from the activity's `activity_locations` (the single
 * source of truth) for the `activityId` currently selected in the panel. In
 * create mode the panel owns that selection and reports it via `onActivityChange`,
 * so the host lifts it into state and passes it here; in edit mode it is fixed.
 */
export interface SlotLocationPickerProps {
  physicalOptions: PhysicalLocationOption[];
  onlineOptions: OnlineOption[];
  slotLocationTranslations: SlotLocationBlockTranslations;
}

export function useSlotLocationPickerProps(
  activityId: string | undefined,
  opts?: { enabled?: boolean }
): SlotLocationPickerProps {
  const slotLocationTranslations = useSlotLocationPickerTranslations();
  const { data: locations } = useActivityLocations(activityId, { enabled: opts?.enabled ?? true });

  const physicalOptions = useMemo<PhysicalLocationOption[]>(
    () =>
      (locations ?? [])
        .filter((l) => (l.kind === 'venue' || l.kind === 'at_home') && l.isActive !== false)
        .map((l) => ({
          id: l.id,
          kind: l.kind === 'at_home' ? 'at_home' : 'venue',
          // The auto-provisioned base location often carries no label; fall back to
          // its address / city, then the kind name, so the row is never blank.
          label:
            locationName({ label: l.label, hubName: l.hub?.name }) ||
            l.addressLine1 ||
            l.city ||
            (l.kind === 'at_home' ? slotLocationTranslations.kindAtHome : slotLocationTranslations.kindVenue),
          addressLine1: l.addressLine1,
          city: l.city,
          radius: l.radius
        })),
    [locations, slotLocationTranslations.kindAtHome, slotLocationTranslations.kindVenue]
  );

  const onlineOptions = useMemo<OnlineOption[]>(
    () =>
      (locations ?? [])
        .filter((l) => l.kind === 'online' && l.isActive !== false)
        .map((l) => ({
          id: l.id,
          provider: l.meetingProvider ?? 'toptoptime',
          label: l.label || slotLocationTranslations.onlineRoomTitle,
          url: l.meetingUrl
        })),
    [locations, slotLocationTranslations.onlineRoomTitle]
  );

  return {
    physicalOptions,
    onlineOptions,
    slotLocationTranslations
  };
}
